import React from "react";
import { formatDate } from "../utils/dateUtils";

// Shared table for the bureau-side matches view.
// Used by:
//   - ClientReferrals.jsx (detail page filtered to one client)
// The legacy "all referrals" view in ReferralManagement.jsx has been
// replaced by a per-client roll-up — when we need an "all matches"
// view again it can drop this component in too.
//
// `showClient` lets us hide the redundant client column on the
// per-client detail page (every row is the same client there).
const STATUS_COLORS = {
  sent: "bg-blue-100 text-blue-800",
  viewed: "bg-yellow-100 text-yellow-800",
  contacted: "bg-purple-100 text-purple-800",
  interested: "bg-green-100 text-green-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

const ReferralsTable = ({
  referrals,
  onRowClick,
  onDelete,
  showClient = true,
  emptyMessage = "No matches yet.",
}) => {
  if (!referrals || referrals.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-[var(--color-text-muted)] text-lg mb-2">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[var(--color-surface-muted)]">
            <tr>
              {showClient && (
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  Client
                </th>
              )}
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Profile
              </th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Created
              </th>
              {onDelete && (
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {referrals.map((referral) => (
              <tr
                key={referral._id}
                className="group hover:bg-[var(--color-primary)] cursor-pointer transition-colors"
                onClick={() => onRowClick && onRowClick(referral)}
              >
                {showClient && (
                  <td className="px-4 py-3">
                    <div>
                      <div className="text-sm font-semibold text-[var(--color-text-strong)] group-hover:text-[var(--color-on-primary)]">
                        {referral.client?.user?.fullName ||
                          referral.client?.fullName ||
                          "Unknown"}
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)] group-hover:text-[var(--color-on-primary)] group-hover:opacity-90 mt-0.5">
                        {referral.client?.user?.referenceId ||
                          referral.client?.referenceId}
                      </div>
                    </div>
                  </td>
                )}
                <td className="px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-[var(--color-text-strong)] group-hover:text-[var(--color-on-primary)]">
                      {referral.profile?.fullName || "Unknown"}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] group-hover:text-[var(--color-on-primary)] group-hover:opacity-90 mt-0.5">
                      {referral.profile?.referenceId}
                      {referral.profile?.age ? ` • ${referral.profile.age}` : ""}
                      {referral.profile?.city ? ` • ${referral.profile.city}` : ""}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {/* Status pill keeps its semantic color on hover —
                      "accepted green / rejected red" is meaningful,
                      flattening it to white would lose information. */}
                  <div className="flex flex-wrap items-center gap-1">
                    <div
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                        STATUS_COLORS[referral.status] || "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {referral.status}
                    </div>
                    {/* Origin tag — present whenever the Referral was
                        auto-created from an introduced interest request,
                        so the bureau remembers it came from the seeker
                        side, not a manual match they made. */}
                    {referral.sourceInterestRequest && (
                      <span
                        title="Auto-created from an introduced interest request"
                        className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)]"
                      >
                        via interest
                      </span>
                    )}
                  </div>
                  {referral.clientComments &&
                    referral.clientComments.length > 0 && (
                      <div className="text-[var(--color-text-muted)] group-hover:text-[var(--color-on-primary)] group-hover:opacity-90 text-xs mt-1">
                        {referral.clientComments.length} client comment
                        {referral.clientComments.length > 1 ? "s" : ""}
                      </div>
                    )}
                </td>
                <td className="px-4 py-3 text-xs text-[var(--color-text-muted)] group-hover:text-[var(--color-on-primary)] group-hover:opacity-90 whitespace-nowrap">
                  {formatDate(referral.createdAt)}
                </td>
                {onDelete && (
                  <td className="px-4 py-3">
                    {/* Delete button stays red — it's a danger action and
                        needs to read as such regardless of row hover. */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(referral._id);
                      }}
                      className="text-red-500 hover:text-red-700 group-hover:text-white group-hover:hover:text-red-100 transition-colors text-xs font-medium"
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { STATUS_COLORS };
export default ReferralsTable;
